import { useState } from 'react'

import {
  type IntakeFormRefusal,
} from '@/definitions/intakeForm'
import { useCreateIntakeForm } from '@/hooks/useCreateIntakeForm'

import {
  awaitingWarningConfirmation,
  runIntakeFormSave,
} from '../utils/runIntakeFormSave'

// Calling a timeout a failure invites the retry that comes back refusing a name
// the first attempt already took.
const SAVE_UNCONFIRMED_MESSAGE =
  'The server did not answer in time, so whether the form was saved is not known. ' +
  'Close this and check the list before trying again.'

interface UploadCallbacks {
  /** A name the organization already uses — reported on the field that fixes it. */
  onNameRefused: (message: string) => void
  onSaved: (displayName: string) => void
}

interface SubmitArgs {
  displayName: string
  formTemplateJson: string
}

/**
 * The save flow behind the upload modal: what to send, and what each refusal means.
 *
 * Nothing here decides what is acceptable. Only text that is not JSON at all is
 * judged locally; every other verdict comes from the API's refusal.
 */
export const useUploadIntakeForm = (orgId: string, callbacks: UploadCallbacks) => {
  const createForm = useCreateIntakeForm()

  const [refusal, setRefusal] = useState<IntakeFormRefusal | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  // A refusal describes the text it was raised against, so an edit retires it and
  // the next press submits afresh rather than acknowledging warnings never shown.
  const retireRefusal = () => {
    setRefusal(null)
    setProblem(null)
  }

  const warnings = refusal?.warnings ?? []
  const isAwaitingConfirmation = awaitingWarningConfirmation(refusal)

  const submit = ({ displayName, formTemplateJson }: SubmitArgs) =>
    runIntakeFormSave({
      displayName,
      formTemplateJson,
      onNameRefused: callbacks.onNameRefused,
      onSaved: callbacks.onSaved,
      save: async ({ displayName: trimmed, formTemplate }) => {
        await createForm.mutateAsync({
          orgId,
          data: {
            display_name: trimmed,
            form_template: formTemplate,
            acknowledge_warnings: isAwaitingConfirmation,
          },
        })
        return trimmed
      },
      setProblem,
      setRefusal,
      timeoutMessage: SAVE_UNCONFIRMED_MESSAGE,
    })

  return {
    refusal,
    problem,
    warnings,
    isAwaitingConfirmation,
    isSaving: createForm.isPending,
    retireRefusal,
    submit,
  }
}
